// @flow
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { get, isEqual } from 'lodash';
import { logout, getUser } from '@context/user';
import { mapBundledPermission } from '@helpers/connect';
import { Api } from '../helpers';
import { router } from '../routes/router';
import MemedSettingsView from '../components/views/Settings/Prescriptions/MemedSettings/MemedSettings';
import type { MemedAccount, MemedForm, MemedLoadError } from '../components/views/Settings/Prescriptions/MemedSettings/MemedSettings';

// "Nova Receita Memed" sends an unconnected doctor here with ?voltar=<the chart>; only a chart
// path is followed back
const backPath = (search: string) => {
  const path = new URLSearchParams(search).get('voltar');
  return path && /^\/paciente\/\d+\/prontuario$/.test(path) ? path : null;
};

const MemedSettings = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(state => get(state, ['context', 'user']));
  const permissions = useSelector(state => mapBundledPermission(state), isEqual);
  const planIncludesMemed = Boolean(get(permissions, ['planInclude', 'newMemed']));
  const storedConnected = get(user, ['advancedData', 'memedConnected']);
  const storedConnectedRef = useRef(storedConnected);
  storedConnectedRef.current = storedConnected;

  const [account, setAccount] = useState<?MemedAccount>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<?MemedLoadError>(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const back = useMemo(() => backPath(location.search), [location.search]);

  const load = useCallback(() => {
    setIsLoading(true);
    setLoadError(null);
    Api.get(router.getApiRoute('memedAccount'), {
      success: (response) => {
        const memedAccount = get(response, 'memedAccount');
        setAccount(memedAccount);
        setIsLoading(false);
        // Connected or disconnected elsewhere (support, another tab): the user reloads, so "Nova Receita Memed" agrees
        const stored = storedConnectedRef.current;
        if (memedAccount && stored !== undefined && memedAccount.connected !== stored) dispatch(getUser());
      },
      error: (err) => {
        setLoadError({ status: get(err, 'status'), message: err.message });
        setIsLoading(false);
      },
    }).catch(() => {}); // handled in error above
  }, [dispatch]);

  useEffect(() => {
    if (planIncludesMemed) load();
    else setIsLoading(false);
  }, [load, planIncludesMemed]);

  const handleSubmit = useCallback((form: MemedForm) => {
    setIsSaving(true);
    setError(null);
    setFieldErrors({});
    Api.put(router.getApiRoute('memedAccount'), {
      data: {
        memed: {
          name: form.name,
          cpf: form.cpf,
          birthDate: form.birthDate,
          councilDoc: form.councilDoc,
          councilState: form.councilState,
          gender: form.gender,
        },
      },
      success: (response) => {
        setAccount(get(response, 'memedAccount'));
        setIsSaving(false);
        // Also when the doctor already left the screen: "Nova Receita Memed" opens Memed from now on
        dispatch(getUser());
      },
      error: (err) => {
        setError(err.message);
        setFieldErrors(get(err, ['data', 'fieldErrors']) || {});
        setIsSaving(false);
      },
    }).catch(() => {}); // handled in error above
  }, [dispatch]);

  const handleBack = useCallback(() => {
    if (back) history.push(back);
  }, [back, history]);

  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);

  return (
    <MemedSettingsView
      user={user}
      permissions={permissions}
      onLogout={handleLogout}
      account={account}
      isLoading={isLoading}
      isSaving={isSaving}
      loadError={loadError}
      error={error}
      fieldErrors={fieldErrors}
      onReload={load}
      onSubmit={handleSubmit}
      onBack={back ? handleBack : null}
    />
  );
};

export default MemedSettings;
